import { setSchemaDefaults, isEmptyObject } from "@utils/object-helpers";
import cloneDeep from "lodash/cloneDeep";
import { mergeAdvanced } from "object-merge-advanced";
import { camelToTitle } from "@utils/string-helpers";
import { getESTNow, getNowISO } from "@utils/time-helpers";
import moment from "moment-timezone";
import isEqual from "lodash/isEqual";
import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import { MIN_SCHEDULE_WINDOW_MINUTES } from "@constants/index";

/**
 * Returns true when `iso` is at least `minMinutes` in the future relative to `now`.
 * `now` is injectable so callers (and tests) can supply a deterministic clock;
 * `null`/`undefined`/invalid inputs return false.
 */
export const scheduledTimeMeetsMinWindow = (
  iso,
  { minMinutes = MIN_SCHEDULE_WINDOW_MINUTES, now = getESTNow() } = {}
) => {
  if (!iso) return false;
  const scheduled = moment(iso);
  if (!scheduled.isValid()) return false;
  return scheduled.diff(now, "minutes", true) >= minMinutes;
};

/**
 * A release version has a real config only when its `files_details` includes a
 * platform `config` file; metadata-only entries (enabled but never created)
 * carry just `release_version_meta_data`.
 */
export const releaseHasConfig = (releaseVersion) =>
  Array.isArray(releaseVersion?.files_details) &&
  releaseVersion.files_details.some((file) => file?.filename === "config");

export const formatSypiLoggedChanges = (loggedChanges) =>
  loggedChanges && Array.isArray(loggedChanges)
    ? `[${loggedChanges.join(", ")}]`
    : `\n ${Object.keys(loggedChanges).map(
        (rootKey) =>
          `${rootKey}: ${Object.keys(loggedChanges[rootKey])
            .map((subKeys) => `${subKeys}`)
            .join(", ")}`
      )}`;

export const generateBlankConfig = (schemaParam, defaultOverride = null) => {
  let schemaTopLevel = schemaParam.properties;
  return Object.keys(schemaTopLevel).reduce((acc, cur) => {
    if (schemaTopLevel.hasOwnProperty(cur)) {
      acc[cur] = setSchemaDefaults(
        schemaTopLevel[cur].properties,
        defaultOverride ? defaultOverride : cur !== "features" ? "" : false,
        schemaParam.definitions || schemaParam.$defs,
        defaultOverride
      );
    }
    return acc;
  }, {});
};
export const mergeLoggedChanges = (current_config, changeDict) => {
  let updatedSypiConfig =
    changeDict && !isEmptyObject(changeDict)
      ? Object.keys(changeDict).reduce((accSypiConfig, path) => {
          if (has(current_config, path)) {
            set(accSypiConfig, path, changeDict[path]);
          }
          return accSypiConfig;
        }, current_config)
      : current_config;
  return updatedSypiConfig;
};

export const compareConfigs = (
  template,
  loadedConfig,
  customCondition = null
) => {
  return mergeAdvanced(template, loadedConfig, {
    cb: (inputArg1, inputArg2, resultAboutToBeReturned) =>
      typeof resultAboutToBeReturned === "object"
        ? resultAboutToBeReturned
        : customCondition !== null
        ? customCondition
        : !isEqual(inputArg1, inputArg2)
        ? resultAboutToBeReturned
        : !inputArg2,
  });
};

export const formatErrors = (errors) => {
  return errors.length
    ? errors.reduce((acc, val) => {
        let errorItem = val.split(".");
        if (!acc.hasOwnProperty(errorItem[0])) {
          acc[errorItem[0]] = [errorItem[1]];
        } else if (!acc[errorItem[0]].includes(errorItem[1])) {
          acc[errorItem[0]].push(errorItem[1]);
        }
        return acc;
      }, {})
    : {};
};

export const shouldSummaryFormRequirementsComplete = (
  configSchedules,
  requestedStatus,
  initialStatus,
  release_version
) => {
  const transitionStatus = getAllowedTrasitionStatus(
    configSchedules,
    release_version,
    true,
    true
  );

  const transitionStatusRequested =
    requestedStatus === "completed" || initialStatus === "completed"
      ? "completed"
      : requestedStatus === "scheduled"
      ? initialStatus === "scheduled"
        ? transitionStatus.scheduleToSchedule
        : initialStatus === "draft"
        ? transitionStatus.draftToSchedule
        : transitionStatus.newToSchedule
      : requestedStatus === "draft"
      ? initialStatus === "draft"
        ? transitionStatus.draftToDraft
        : !initialStatus
        ? transitionStatus.newToDraft
        : false
      : false;

  return transitionStatusRequested;
};

export const getAllowedTrasitionStatus = (
  configSchedules,
  release_version,
  scheduleHasLimit = false,
  draftHasLimit = false
) => {
  const updatedStatus = configSchedules
    .filter(
      (configSchedule) =>
        (configSchedule?.release_version || configSchedule?.release_id) ===
        release_version
    )
    .reduce(
      (allowed, configSchedule) => {
        allowed[configSchedule.status] = allowed[configSchedule.status] + 1;
        return allowed;
      },
      { scheduled: 0, draft: 0, completed: 0 }
    );

  return {
    newToDraft: !(draftHasLimit && updatedStatus.draft),
    newToSchedule: !(scheduleHasLimit && updatedStatus.scheduled),
    draftToSchedule: !(scheduleHasLimit && updatedStatus.scheduled),
    draftToDraft: true,
    scheduleToSchedule: true,
    completed: true,
  };
};

export const mergeSypiVersionChanges = (
  changeLog,
  sypiVersion,
  initialSypiConfig,
  logAdditionalDetails = {}
) => {
  const updatedChangeLog = Object.keys(changeLog);
  const summaryReview = [];
  updatedChangeLog.forEach((change) => {
    const modifiedSection = camelToTitle(change.split(".")[0]);
    const newKeyValue = changeLog[change];
    const oldKeyValue = get(initialSypiConfig, change);
    const updatedNewKeyValue = Array.isArray(newKeyValue)
      ? newKeyValue.join(", ")
      : typeof newKeyValue === "object" && newKeyValue !== null
      ? Object.keys(newKeyValue).join(", ")
      : newKeyValue ?? "";

    const updatedOldKeyValue = Array.isArray(oldKeyValue)
      ? oldKeyValue.join(", ")
      : typeof oldKeyValue === "object" && oldKeyValue !== null
      ? Object.keys(oldKeyValue).join(", ")
      : oldKeyValue ?? "";

    if (updatedNewKeyValue !== updatedOldKeyValue)
      summaryReview.push({
        section: modifiedSection,
        keyName: change,
        newKeyValue: updatedNewKeyValue,
        oldKeyValue: updatedOldKeyValue,
        ...(logAdditionalDetails && logAdditionalDetails[change]
          ? logAdditionalDetails[change]
          : {}),
      });
  });
  return summaryReview;
};

export const configSchedulerEnrichment = (
  storeContents,
  isScheduled,
  modifiedSections,
  importedConfig = {}
) => {
  const { config, sypiNewView, sypiConfigScheduler, sypiSchema } =
    storeContents;
  const {
    name,
    displayName,
    liveScheduleDate,
    oldScheduleDate,
    status,
    summaryView,
  } = sypiConfigScheduler.scheduleDraft;
  const { schemaDefaults } = sypiConfigScheduler;
  const { rawMasterSchema, activeReleaseVersion, selectedReleaseVersions } =
    sypiSchema;
  const sypiS3HasChnaged =
    sypiNewView.loggedChanges && sypiNewView.loggedChanges.length;
  const schemaDefaultSummaryView =
    schemaDefaults?.[activeReleaseVersion] &&
    schemaDefaults?.[activeReleaseVersion].include
      ? schemaDefaults?.[activeReleaseVersion].preview
      : [];
  const { newConfig, release_version, newSummaryView, changeDict } =
    importedConfig || null;
  return {
    release_version: release_version || activeReleaseVersion,
    configSchedules: {
      name: `${name}${
        selectedReleaseVersions.length > 1
          ? ` (${release_version || activeReleaseVersion})`
          : ""
      }`,
      displayName,
      liveScheduleDate,
      oldScheduleDate,
      status,
      modifiedDate: getNowISO(config.timeZone, "YYYY-MM-DDTHH:mm:ss"),
      sectionsModified: Object.keys(modifiedSections),
      summaryView: [
        ...(!isEmptyObject(importedConfig) ? newSummaryView : summaryView),
        ...(!release_version && schemaDefaultSummaryView.length
          ? schemaDefaultSummaryView
          : []),
      ],
      release_version: release_version || activeReleaseVersion,
      schemaPath: {
        ...(sypiS3HasChnaged
          ? {
              sypiS3: !isEmptyObject(importedConfig)
                ? changeDict
                : sypiNewView.changeDict,
            }
          : {}),
      },
      ...(isScheduled
        ? {
            sypiConfigs: {
              ...(sypiS3HasChnaged
                ? {
                    sypiS3: !isEmptyObject(importedConfig)
                      ? newConfig
                      : sypiNewView.sypiConfig,
                  }
                : {}),
              ...(rawMasterSchema && !isEmptyObject(rawMasterSchema)
                ? { master_schema: rawMasterSchema, schemaUpdate: true }
                : {}),
            },
          }
        : {}),
    },
  };
};

export const getModifiedSections = (sypiS3Change) => {
  let modifiedSypiSections = {};
  return sypiS3Change && !isEmptyObject(sypiS3Change)
    ? Object.keys(sypiS3Change).reduce((acc, item) => {
        let section = item && item.length ? item.split(".")[0] : "";
        if (section) {
          acc[section] = section;
        }
        return acc;
      }, modifiedSypiSections)
    : modifiedSypiSections;
};

export const setDisplayCondition = (objectTree, concatPath = "") => {
  return Object.keys(objectTree).reduce((acc, cur) => {
    if (objectTree[cur] && typeof objectTree[cur] === "object") {
      const subKeys = Object.keys(objectTree[cur]);
      // Empty sub-tree: treat as "no signal yet" and default to expanded rather than vacuously collapsed.
      acc[cur] =
        subKeys.length > 0 &&
        subKeys.every(
          (key) =>
            typeof objectTree[cur][key] === "boolean" && !objectTree[cur][key]
        );
      set(acc, `${cur}.defaultDisplay`, !acc[cur]);
      if (!get(acc, `${concatPath}.defaultDisplay`) && !acc[cur]) {
        set(acc, `${concatPath}.defaultDisplay`, true);
      }
      let path = concatPath ? `${concatPath}.${cur}` : cur;
      acc[cur] = { ...acc[cur], ...setDisplayCondition(objectTree[cur], path) };
    } else {
      acc[cur] = objectTree[cur];
    }
    return acc;
  }, {});
};

/**
 *
 * @param {Array} scheduledChanges scheduled changes
 * @returns {Object} keysOccurrence
 * @description Takes in the current scheduled changes, counts and consolidates all keys into an object, returns object
 */
export const countOccurrenceOfKeyInScheduledChanges = (
  scheduledChanges = []
) => {
  const flatKeys = scheduledChanges
    ? scheduledChanges.reduce((acc, { schemaPath }) => {
        acc.push(...Object.keys(schemaPath.sypiS3 ? schemaPath.sypiS3 : {}));
        return acc;
      }, [])
    : [];
  const keysOccurrence = flatKeys.reduce((acc, keyPath) => {
    acc[keyPath] = (acc[keyPath] ? acc[keyPath] : 0) + 1;
    return acc;
  }, {});
  return keysOccurrence;
};

/**
 *
 * @param {Object} changeLog change log - unsaved changes or scheduled changes opened for editing
 * @param {Array} scheduledChanges scheduled changes
 * @description Reports if 3 or more scheduled changes are modifying a specific key
 */
export const currentKeysOccurrenceChangesReport = (
  changeLog,
  scheduledChanges
) => {
  const keyOccurrenceOnScheduledChanges =
    countOccurrenceOfKeyInScheduledChanges(scheduledChanges);
  const keyOccurrenceLimit = 3;
  const keysOverLimit = Object.entries(changeLog).filter(
    ([keyPath, value]) =>
      keyOccurrenceOnScheduledChanges[keyPath] &&
      keyOccurrenceOnScheduledChanges[keyPath] >= keyOccurrenceLimit
  );
  const scheduledChangesWithCommonKeys = Object.fromEntries(keysOverLimit);
  const successFailedKeysReport = Object.entries(
    scheduledChangesWithCommonKeys
  ).reduce((acc, [keyPath, value]) => {
    acc[keyPath] = { disable: true };
    return acc;
  }, {});
  return {
    scheduledChangesWithCommonKeys: scheduledChangesWithCommonKeys,
    successFailedKeysReport,
  };
};
